import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X, ImageOff, Wand2, Gift, ShoppingBag, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatPrice, resolveAssetUrl } from "../lib/api";
import { useAuthStore } from "../store/auth";
import { useCartStore } from "../store/cart";
import { useCurrencyStore } from "../store/currency";
import toast from "react-hot-toast";

// ─── Constants ────────────────────────────────────────────────────────────────

const STYLES = [
  { id: "natural",  label: "Natural",  filter: "none" },
  { id: "vintage",  label: "Vintage",  filter: "sepia(0.45) contrast(1.1) brightness(0.95)" },
  { id: "vivid",    label: "Vivid",    filter: "saturate(1.6) contrast(1.1)" },
  { id: "cool",     label: "Cool",     filter: "hue-rotate(20deg) saturate(1.2) brightness(1.05)" },
  { id: "warm",     label: "Warm",     filter: "sepia(0.2) saturate(1.3) hue-rotate(-10deg)" },
  { id: "dramatic", label: "Dramatic", filter: "contrast(1.4) brightness(0.8) saturate(0.85)" },
];

const PRESETS = [
  { id: "none", label: "No Preset", prompt: "" },
  { id: "animation", label: "Animation", prompt: "in a vibrant 3D animation style, Pixar inspired, colorful" },
  { id: "realistic", label: "Realistic", prompt: "photorealistic, cinematic lighting, 8k resolution, highly detailed" },
  { id: "oil", label: "Oil Painting", prompt: "as a classic oil painting, rich brushstrokes, textured canvas" },
  { id: "watercolor", label: "Watercolor", prompt: "ethereal watercolor style, soft edges, artistic washes" },
  { id: "sketch", label: "Pencil Sketch", prompt: "detailed pencil sketch, hand-drawn, graphite texture" },
];

interface GeneratedImage {
  preview: string;
  source: string;
  orientation: "square" | "portrait" | "landscape";
  prompt: string;
  greetingText: string;
  textPlacement: "top" | "bottom" | "left" | "right";
  preset: string;
  templateId: string | null;
  style: string;
}

const AI_ENABLED = import.meta.env.VITE_AI_CARDS_ENABLED === "true";

// ─── Prohibited Trademarks ───────────────────────────────────────────────────

const PROHIBITED_TRADEMARKS = [
  "Lego", "Disney", "Nintendo", "Mickey Mouse", "Marvel", "Star Wars", "Pokemon", 
  "Pikachu", "Hello Kitty", "Sanrio", "Studio Ghibli", "Totoro", "Harry Potter", 
  "Barbie", "Minecraft", "Fortnite", "Batman", "Superman", "Spiderman", "Iron Man",
  "Transformers", "Lego City", "Lego Star Wars", "Lego Marvel"
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function AiCardCreator() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { fetch: fetchCart } = useCartStore();
  const { currency, aiEnabled, siteMode } = useCurrencyStore();

  const [open, setOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [cardPrices, setCardPrices] = useState<Record<string, number>>({ USD: 15 });
  const [templates, setTemplates] = useState<{ id: string; name: string; imageUrl: string }[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  // Generation
  const [step, setStep] = useState<1 | 2>(1);
  const [prompt, setPrompt] = useState("");
  const [greetingText, setGreetingText] = useState("");
  const [textPlacement, setTextPlacement] = useState<"top" | "bottom" | "left" | "right">("bottom");
  const [preset, setPreset] = useState("none");
  const [orientation, setOrientation] = useState<"square" | "portrait" | "landscape">("square");
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [sourceImageUrl, setSourceImageUrl] = useState<string | null>(null);

  // Legal
  const [trademarkWarning, setTrademarkWarning] = useState<string | null>(null);

  // Editor
  const [style, setStyle] = useState("natural");
  const [saving, setSaving] = useState(false);

  // Persistence: History
  const fetchHistory = () => {
    if (!user) return;
    api.get("/ai-cards/history").then((r) => {
      setGeneratedImages(r.data);
      // Auto-load latest if nothing selected
      if (r.data.length > 0 && !imageUrl) {
        const latest = r.data[0];
        setImageUrl(latest.preview);
        setSourceImageUrl(latest.source);
        setOrientation(latest.orientation);
        setPrompt(latest.prompt);
        setGreetingText(latest.greetingText);
        setTextPlacement(latest.textPlacement);
        setPreset(latest.preset);
        setSelectedTemplate(latest.templateId);
        setStyle(latest.style);
      }
    }).catch(() => {});
  };

  useEffect(() => {
    if (open && user) {
      fetchHistory();
    }
  }, [open, user]);

  // Scroll Lock
  useEffect(() => {
    const isLocked = open || promoOpen || !!trademarkWarning;
    if (isLocked) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, promoOpen, trademarkWarning]);

  useEffect(() => {
    if (!open || !user) return;
    api.get("/ai-cards/status").then((r) => {
      setCredits(r.data.credits);
      setCardPrices(r.data.cardPrices || { USD: r.data.cardPrice || 15 });
    }).catch(() => {});

    api.get("/ai-cards/templates").then((r) => {
      setTemplates(r.data);
      if (r.data.length > 0 && !selectedTemplate) setSelectedTemplate(r.data[0].id);
    }).catch(() => {});
  }, [open, user, selectedTemplate]);

  if (!AI_ENABLED || !aiEnabled) return null;

  // ─── Trademark Check ───────────────────────────────────────────────────────

  function checkTrademarks(text: string): string | null {
    const found = PROHIBITED_TRADEMARKS.find(tm => 
      new RegExp(`\\b${tm.replace(/ /g, '\\s+')}\\b`, "i").test(text)
    );
    return found || null;
  }

  // ─── Generate ──────────────────────────────────────────────────────────────

  async function generate() {
    if (!prompt.trim() || credits <= 0 || generating) return;

    const detectedTrademark = checkTrademarks(prompt) || checkTrademarks(greetingText);
    if (detectedTrademark) {
      setTrademarkWarning(detectedTrademark);
      return;
    }

    setGenerating(true);
    try {
      const { data } = await api.post("/ai-cards/generate", { 
        prompt: prompt.trim(),
        greetingText: greetingText.trim() || undefined,
        textPlacement,
        preset: preset !== "none" ? preset : undefined,
        orientation
      });
      
      const newGen: GeneratedImage = {
        preview: data.previewUrl,
        source: data.originalUrl,
        orientation,
        prompt: prompt.trim(),
        greetingText: greetingText.trim(),
        textPlacement,
        preset,
        templateId: selectedTemplate,
        style
      };
      
      // Update UI immediately
      setImageUrl(data.previewUrl);
      setSourceImageUrl(data.originalUrl);
      setGeneratedImages((prev) => [newGen, ...prev].slice(0, 12));
      
      setCredits((c) => c - 1);
      setStyle("natural");
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  // ─── Add to cart ───────────────────────────────────────────────────────────

  async function addToCart() {
    if (!imageUrl || !sourceImageUrl || saving) return;
    setSaving(true);
    try {
      // Ensure we have a proper URL for fetch.
      const fullImageUrl = resolveAssetUrl(imageUrl);
      const imgRes = await fetch(fullImageUrl); 
      const blob = await imgRes.blob();
      const objectUrl = URL.createObjectURL(blob);
      const img = new window.Image();
      await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = objectUrl; });

      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;

      const styleFilter = STYLES.find((s) => s.id === style)?.filter ?? "none";
      if (styleFilter !== "none") ctx.filter = styleFilter;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.filter = "none";
      URL.revokeObjectURL(objectUrl);

      // JPEG for cart preview
      const imageData = canvas.toDataURL("image/jpeg", 0.9);

      const { data: saved } = await api.post("/ai-cards/save", { 
        imageData, 
        sourceImageUrl,
        templateId: selectedTemplate,
        prompt,
        greetingText,
        textPlacement,
        preset,
        orientation,
        style
      });
      await api.post("/cart", { productId: saved.productId, quantity: 1 });
      await fetchCart();
      fetchHistory(); // Refresh history as save might update metadata

      toast.success("Added to cart! By ordering, you confirm ownership of the content and indemnify Chlorine GK against claims.", { duration: 5000 });
      setOpen(false);
      setStep(1);
      setGreetingText("");
      setImageUrl(null);
      setSourceImageUrl(null);
    } catch (err: any) {
      console.error("[AI Cart]", err);
      toast.error(err?.response?.data?.error ?? "Failed to add to cart. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const cssFilter = STYLES.find((s) => s.id === style)?.filter ?? "none";
  const previewRatio = orientation === "portrait" ? 1 / 1.414 : orientation === "landscape" ? 1.414 : 1;
  const currentCardPrice = cardPrices[currency] || cardPrices["USD"] || 15;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <button
        onClick={() => { if (!user) { setPromoOpen(true); return; } setOpen(true); }}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
        style={{ background: "rgb(var(--brand-color))" }}
        title={t("aiCard.fabLabel")}
      >
        <Sparkles className="text-white" size={24} />
      </button>

      {promoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setPromoOpen(false)}>
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 pt-8 pb-6 text-center" style={{ background: "rgb(var(--brand-color))" }}>
              <Sparkles size={40} className="mx-auto mb-3 text-white" />
              <h2 className="font-display text-2xl font-bold text-white mb-2">{t("aiCard.promoDialogTitle")}</h2>
              <p className="text-white/80 text-sm leading-relaxed">{t("aiCard.promoDialogSub")}</p>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <Gift size={20} className="flex-shrink-0 mt-0.5" style={{ color: "rgb(var(--brand-color))" }} />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{t("aiCard.promoPerk1Title")}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t("aiCard.promoPerk1Body")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <ShoppingBag size={20} className="flex-shrink-0 mt-0.5" style={{ color: "rgb(var(--brand-color))" }} />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{t("aiCard.promoPerk2Title")}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t("aiCard.promoPerk2Body")}</p>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 flex flex-col gap-2">
              <Link to="/register" onClick={() => setPromoOpen(false)} className="btn-primary w-full text-center">{t("aiCard.promoDialogCta")}</Link>
              <Link to="/login" onClick={() => setPromoOpen(false)} className="text-center text-sm text-gray-500 hover:text-gray-700 py-1">{t("aiCard.promoDialogSignIn")}</Link>
            </div>
            <button onClick={() => setPromoOpen(false)} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"><X size={20} /></button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="bg-white w-full sm:max-w-6xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden h-full sm:h-[90vh] max-h-full">
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles size={17} style={{ color: "rgb(var(--brand-color))" }} />
                <h2 className="font-semibold text-gray-900 text-sm">{t("aiCard.title")}</h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                  {t("aiCard.credits", { count: credits })}
                </span>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={20} /></button>
              </div>
            </div>

            {/* Body Container */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              
              <div className="flex-1 flex flex-col min-h-0">
                {step === 1 ? (
                  <div className="flex-1 flex flex-col sm:flex-row min-h-0 overflow-y-auto sm:overflow-hidden">
                    {/* LEFT: Preview */}
                    <div className="flex flex-col sm:flex-1 border-b sm:border-b-0 sm:border-r border-gray-100 bg-gray-50 overflow-y-visible sm:overflow-y-auto">
                      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 min-h-[320px]">
                        {!imageUrl ? (
                          <div className="text-center text-gray-400 space-y-3 pointer-events-none select-none">
                            <ImageOff size={52} className="mx-auto opacity-20" />
                            <p className="text-sm">{t("aiCard.previewEmpty")}</p>
                          </div>
                        ) : (
                          <div className="relative rounded-2xl overflow-hidden shadow-xl select-none w-full" style={{ maxWidth: orientation === "landscape" ? 600 : 500, aspectRatio: `${previewRatio}`, margin: "0 auto" }}>
                            <img src={resolveAssetUrl(imageUrl)} alt="AI generated card" className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ filter: cssFilter }} draggable={false} />
                            <div className="absolute inset-0 pointer-events-none select-none flex items-center justify-center">
                              <span className="font-bold uppercase tracking-[0.4em] text-xl" style={{ transform: "rotate(-35deg)", color: "rgba(255,255,255,0.18)", textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>PREVIEW</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="px-4 pb-4 pt-2 flex-shrink-0 border-t border-gray-100 bg-white">
                        <button onClick={() => setStep(2)} disabled={!imageUrl} className="btn-primary w-full">
                          {imageUrl ? "Next: Choose Style & Preview" : t("aiCard.generateFirst")}
                        </button>
                      </div>
                    </div>

                    {/* RIGHT: Controls */}
                    <div className="w-full sm:w-80 flex-shrink-0 p-4 sm:p-5 space-y-4 sm:space-y-6 border-t sm:border-t-0 sm:overflow-y-auto">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t("aiCard.promptLabel")}</label>
                        <textarea className="input text-sm resize-none" rows={4} placeholder={t("aiCard.promptPlaceholder")} value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={generating} />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Greeting Text (Inside Image)</label>
                        <input className="input text-sm" placeholder="e.g. Happy Birthday!" value={greetingText} onChange={(e) => setGreetingText(e.target.value)} disabled={generating} />
                        {greetingText && (
                          <div className="mt-2">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Text Placement</label>
                            <div className="grid grid-cols-4 gap-1">
                              {(["top", "bottom", "left", "right"] as const).map((p) => (
                                <button key={p} onClick={() => setTextPlacement(p)} className={`py-1 rounded text-[10px] font-bold border transition-colors ${textPlacement === p ? "bg-brand-600 border-brand-600 text-white" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                                  {p.toUpperCase()}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Style</label>
                          <select className="input text-[10px] py-1.5" value={preset} onChange={(e) => setPreset(e.target.value)} disabled={generating}>
                            {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Format</label>
                          <select className="input text-[10px] py-1.5" value={orientation} onChange={(e) => setOrientation(e.target.value as any)} disabled={generating}>
                            <option value="square">Square</option>
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                          </select>
                        </div>
                      </div>
                      <button onClick={generate} disabled={generating || !prompt.trim() || credits <= 0} className="btn-primary w-full mt-2 text-sm flex items-center justify-center gap-2">
                        <Wand2 size={15} />
                        {generating ? t("aiCard.generating") : credits <= 0 ? t("aiCard.noCredits") : t("aiCard.generate")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col sm:flex-row min-h-0 overflow-y-auto sm:overflow-hidden">
                    {/* LEFT: Card Layout */}
                    <div className="flex-1 bg-gray-100 p-3 sm:p-6 flex flex-col items-center justify-center overflow-y-auto">
                      <div className="mb-4 sm:mb-6 text-center">
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest">Physical Card Layout (A5 Folded)</p>
                        <p className="text-[9px] text-gray-500 mt-1">Left side is the back • Right side is the front</p>
                      </div>
                      <div className="relative bg-white shadow-2xl flex border border-gray-200 w-full" style={{ maxWidth: orientation === "landscape" ? "500px" : "320px", aspectRatio: "1.414 / 1" }}>
                        <div className="flex-1 border-r border-dashed border-gray-300 relative">
                          {selectedTemplate ? (
                            <img src={templates.find(t => t.id === selectedTemplate)?.imageUrl} className="w-full h-full object-cover" alt="Card Back" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-50">
                              <span className="text-[10px] text-gray-300 uppercase tracking-widest">Brand Mark</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 relative">
                          <img src={resolveAssetUrl(imageUrl)} className="w-full h-full object-cover" style={{ filter: cssFilter }} alt="Card Front" />
                        </div>
                      </div>
                      <button onClick={() => setStep(1)} className="mt-8 text-xs font-semibold text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2">← Back to Generation</button>
                    </div>
                    {/* RIGHT: Editor */}
                    <div className="w-full sm:w-80 flex-shrink-0 p-3 sm:p-5 space-y-4 sm:space-y-8 bg-white border-l border-gray-100 sm:overflow-y-auto">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Choose Card Back Style</label>
                        <div className="grid grid-cols-4 sm:grid-cols-2 gap-x-2 gap-y-3">
                          {templates.map((t) => (
                            <button key={t.id} onClick={() => setSelectedTemplate(t.id)} className="flex flex-col gap-1.5 text-left group">
                              <div className={`relative rounded-lg overflow-hidden aspect-square border-2 transition-all ${selectedTemplate === t.id ? "border-brand-600 shadow-md scale-[1.02]" : "border-gray-100 opacity-60 group-hover:opacity-100"}`}>
                                <img src={t.imageUrl} alt={t.name} className="w-full h-full object-cover" />
                              </div>
                              <p className={`text-[7px] font-bold truncate transition-colors ${selectedTemplate === t.id ? "text-brand-700" : "text-gray-400 group-hover:text-gray-600"}`}>{t.name}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Artistic Filter</label>
                        <div className="flex flex-wrap gap-1">
                          {STYLES.map((s) => (
                            <button key={s.id} onClick={() => setStyle(s.id)} className="px-2 py-1 rounded-full text-[10px] font-medium border transition-all" style={style === s.id ? { borderColor: "rgb(var(--brand-color))", background: "rgb(var(--brand-color))", color: "#fff" } : { borderColor: "#e5e7eb", color: "#6b7280" }}>{s.label}</button>
                          ))}
                        </div>
                      </div>
                      <div className="pt-3 sm:pt-4 border-t border-gray-100">
                        <button onClick={addToCart} disabled={saving || !imageUrl || siteMode === "read_only"} className="btn-primary w-full py-3 sm:py-4 text-sm sm:text-base shadow-xl hover:shadow-2xl transition-all">
                          {saving ? t("aiCard.saving") : siteMode === "read_only" ? "Orders Disabled" : t("aiCard.addToCart", { price: formatPrice(currentCardPrice, currency) })}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Shared History Footer */}
              {generatedImages.length > 0 && (
                <div className="flex-shrink-0 border-t border-gray-100 bg-white px-5 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("aiCard.previousLabel") || "Recent Generations"}</p>
                    {generatedImages.length > 6 && <span className="text-[9px] text-gray-400 font-medium italic">Scroll for more →</span>}
                  </div>
                  <div className="flex gap-2.5 overflow-x-auto pb-1">
                    {generatedImages.map((img, i) => (
                      <button key={i} onClick={() => { 
                        setImageUrl(img.preview); 
                        setSourceImageUrl(img.source);
                        setOrientation(img.orientation);
                        setPrompt(img.prompt);
                        setGreetingText(img.greetingText);
                        setTextPlacement(img.textPlacement);
                        setPreset(img.preset);
                        setSelectedTemplate(img.templateId);
                        setStyle(img.style);
                      }} className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all hover:scale-105 active:scale-95 border-2" style={imageUrl === img.preview ? { borderColor: `rgb(var(--brand-color))` } : { borderColor: "transparent" }}>
                        <img src={resolveAssetUrl(img.preview)} alt="" className="w-full h-full object-cover" draggable={false} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {trademarkWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Trademark Detected</h3>
              <p className="text-sm text-gray-600 leading-relaxed">I noticed your design includes a third-party trademark (<strong>{trademarkWarning}</strong>).</p>
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">Chlolo Designs operates strictly as a print service. Please ensure you possess the necessary commercial rights for this content before proceeding, as Chlolo Designs cannot be held responsible for infringement.</p>
            </div>
            <div className="p-6 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setTrademarkWarning(null)} className="btn-primary w-full py-3">I Understand</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function AiCardPromoBanner() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const { aiEnabled } = useCurrencyStore();
  if (!AI_ENABLED || !aiEnabled) return null;
  return (
    <>
      <section className="bg-brand-50 border-y border-brand-100 py-12 relative overflow-hidden group">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex-1 text-center md:text-left space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-bold uppercase tracking-wider">
              <Sparkles size={14} /> New Feature
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900">{t("aiCard.promoTitle")}</h2>
            <p className="text-gray-600 max-w-xl leading-relaxed">{t("aiCard.promoSub")}</p>
          </div>
          <div className="flex-shrink-0">
            <button onClick={() => { if (!user) { window.location.href = "/register"; return; } setOpen(true); }} className="btn-primary px-8 py-4 text-lg shadow-xl shadow-brand-100 flex items-center gap-3">
              <Sparkles size={22} /> {t("aiCard.promoCta")}
            </button>
          </div>
        </div>
        <div className="absolute -top-12 -right-12 w-64 h-64 bg-brand-100/50 rounded-full blur-3xl group-hover:bg-brand-100 transition-colors" />
      </section>
      {open && <AiCardCreator />}
    </>
  );
}
